import React, { useEffect, useMemo, useState } from 'react';
import { useFeedsAigcCard } from '../Aigc/hooks/feedsAigcCard';
import AigcCard from '../Aigc/card';
import Aigc from '../Aigc';
import { getFeedsStats, getOverviewTopicGptSummary } from '@op/services';
import { getLocale, useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import CardCommon from '../CardCommon';
import { percentageFormat, reviewsFormat } from '@op/utils/value';
import { copyIcon, fullIcon, refreshIcon } from '@op/constants/chart';
import { NO_DATE } from '@op/constants';
import useFullDialog from '../Aigc/hooks/useFullDialog';
import { DBO_T } from '@op/utils/format';

export default function GPTPanelModal({
  gameInfo,
  wholeGameInfo,
  filter,
  otherParams,
  // 初始化默认生成报告不展示弹窗
  showAigcWithoutDialog = false,
  setShowAigcWithoutDialog,
}: any) {
  const {
    showAigcCard,
    setShowAigcCard,
    handleAigcConfirm,
    aigcCardId,
    aigcConfig,
  } = useFeedsAigcCard({
    gameInfo,
    wholeGameInfo,
  });

  const { formatMessage } = useIntl();
  const [type, setType] = useState('');
  const [total, setTotal] = useState(0);
  const [stopGPT, setStopGPT] = useState<null | (() => void)>(null);
  const [showDialog, setShowDialog] = useState(false);
  const { FullDialog, setAigcContent, setVisible, aigcContent } =
    useFullDialog();
  const [defaultGPT, setDefaultGPT] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [GPTError, setGPTError] = useState(false);

  const getFeedsStateValue = (feedsStats: any) => {
    const {
      total = 0,
      bad_total = 0,
      neutral_total = 0,
      good_total = 0,
    } = feedsStats || {};

    let positivePercent = 0;
    let negativePercent = 0;
    let neutralPercent = 0;
    if (total) {
      positivePercent = (good_total / total) * 100;
      negativePercent = (bad_total / total) * 100;
      neutralPercent = (neutral_total / total) * 100;
    }

    return {
      TOTAL_NUM: reviewsFormat(total),
      POS_RATIO: percentageFormat(positivePercent),
      NEU_RATIO: percentageFormat(neutralPercent),
      NEG_RATIO: percentageFormat(negativePercent),
      TOTAL_POS: reviewsFormat(good_total),
      TOTAL_NEU: reviewsFormat(neutral_total),
      TOTAL_NEG: reviewsFormat(bad_total),
    };
  };

  const replaceText = (text: string, replacements: any) => {
    let replacedText = text || '';
    Object.keys(replacements).forEach((key) => {
      const placeholder = `$${key}`;
      replacedText = replacedText.replaceAll(
        placeholder,
        String(replacements[key]),
      );
    });
    return replacedText;
  };

  async function requestData() {
    setLoading(true);
    const { currentTopic } = otherParams;
    const {
      edition_unified_id,
      id_type,
      entity_type,
      start_time,
      end_time,
      topic_level,
    } = filter;
    const feedsState = await getFeedsStats(filter);
    getOverviewTopicGptSummary({
      edition_unified_id,
      id_type,
      entity_type,
      start_time,
      end_time,
      topic_level,
      topic: currentTopic,
    })
      .then((data: any) => {
        const replacements: any = getFeedsStateValue(feedsState);
        const keys: Array<keyof typeof data> = ['report_en', 'report_cn'];
        keys.forEach((key) => {
          data[key] = replaceText(data[key], replacements);
        });
        setDefaultGPT(data);
        setTotal(feedsState?.total || 0);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    requestData();
  }, []);

  useEffect(() => {
    if (showAigcWithoutDialog) {
      setShowDialog(true);
    }
  }, [showAigcWithoutDialog]);

  const { reportText, noDefaultGPT } = useMemo(() => {
    const isEN = getLocale() === LANGUAGE_EN;
    const text = isEN ? defaultGPT?.report_en : defaultGPT?.report_cn || '';

    if (text) {
      setShowAigcCard(true);
    }

    return {
      noDefaultGPT: !text,
      reportText: text,
    };
  }, [formatMessage, defaultGPT]);

  const { operation, text } = useMemo(() => {
    if (GPTError === true) {
      return {
        operation: [refreshIcon],
        text: NO_DATE.EMPTY,
      };
    }
    if (aigcContent !== '' && (showAigcCard || !noDefaultGPT)) {
      return {
        operation: [refreshIcon, copyIcon, fullIcon],
        text: NO_DATE.EMPTY,
      };
    }
    return {
      operation: [],
      text: NO_DATE.EMPTY,
    };
  }, [GPTError, aigcContent, noDefaultGPT, showAigcCard]);

  return (
    <CardCommon
      className="gpt-summary gpt-panel-modal-wrapper"
      contentCard
      loading={loading}
      operations={operation}
      handleOperationsOnClick={(item) => {
        setType('');
        if (item.symbol == 'copy') {
          DBO_T.copyText(aigcContent, formatMessage);
        } else if (item.symbol == 'refresh') {
          if (noDefaultGPT) {
            setShowAigcCard(false);
          }
          setGPTError(false);
          setShowDialog(true);
        } else if (item.symbol === 'fullscreen') {
          setVisible(true);
        }
      }}
      loadingHeight={234}
      title={
        <div className="custom-title">
          {/* <span>{formatMessage({ id: text })}</span>
          <BubbleCommon
            showIcon={false}
            tipsMap={keyOpinionsGPTSummary}
          ></BubbleCommon> */}
        </div>
      }
      operationInfo={[type, setType]}
    >
      {showAigcCard && !showDialog ? (
        <AigcCard
          key={aigcCardId}
          contentCard
          hasTitle={false}
          total={total}
          config={aigcConfig}
          hasDownload={false}
          setAigcContent={setAigcContent}
          defaultSummary={reportText}
          stopAction={() => setShowAigcCard(false)}
          params={filter}
          errAction={() => {
            setGPTError(true);
          }}
          onHandleStop={(handleStop) => setStopGPT(() => handleStop)}
        ></AigcCard>
      ) : (
        <div className="gpt-summary-entry-tips">
          <Aigc
            event={{
              handleAigcConfirm: (data: any) => {
                setDefaultGPT({});
                setShowDialog(false);
                handleAigcConfirm?.(data, filter);
              },
            }}
            showTips
            showBtn={true}
            defaultValue={total}
            showDialog={showDialog}
            topic={otherParams?.topicText}
            setShowDialog={(show: boolean) => setShowDialog(show)}
            showAigcWithoutDialog={showAigcWithoutDialog}
            setShowAigcWithoutDialog={setShowAigcWithoutDialog}
          ></Aigc>
        </div>
      )}
      <FullDialog></FullDialog>
    </CardCommon>
  );
}
